/**
 * Top merchants. Editorial rank list with hairline meters in gold.
 */
import { store } from '../store.ts';
import { formatINR, MERCHANT_DISPLAY } from '../types.ts';

export class HisaabMerchantRank extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.totals.by_merchant, s.summary.totals.inr);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byMerch: Record<string, number>, total: number): void {
    const entries = Object.entries(byMerch).slice(0, 8);
    if (!entries.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const rows = entries.map(([m, v], i) => {
      const pct = total > 0 ? (v / total) * 100 : 0;
      const display = MERCHANT_DISPLAY[m] ?? m.replace(/_/g, ' ');
      return /* html */ `
<div class="row" style="--pct:${pct}%" tabindex="0">
  <div class="rank">${String(i + 1).padStart(2, '0')}</div>
  <div class="info">
    <div class="name">${display}</div>
    <div class="bar"><span></span></div>
  </div>
  <div class="amt">${formatINR(v)}</div>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 14px;
  padding-left: 4px;
}
.list {
  display: grid;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}
.list::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--gold) 0%, transparent 30%);
  opacity: 0.55;
  z-index: 1;
}
.row {
  display: grid;
  grid-template-columns: 38px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 15px 18px;
  border-bottom: 1px solid var(--rule);
  cursor: default;
  transition: background 240ms cubic-bezier(0.32, 0.72, 0, 1);
}
.row:last-child { border-bottom: none; }
.row:hover,
.row:focus-visible {
  background: color-mix(in oklch, var(--gold) 8%, var(--paper-up));
  outline: none;
}
.rank {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--gold);
  letter-spacing: 0.08em;
  font-feature-settings: 'tnum' 1;
}
.info { display: grid; gap: 7px; min-width: 0; }
.name {
  font-family: var(--display);
  font-size: 19px;
  color: var(--ink);
  font-weight: 600;
  letter-spacing: -0.005em;
}
.bar {
  height: 2px;
  background: oklch(20% 0.04 280);
  border-radius: 999px;
  overflow: hidden;
}
.bar span {
  display: block;
  height: 100%;
  width: var(--pct);
  background: linear-gradient(90deg, oklch(72% 0.13 92), var(--gold));
  animation: g 700ms cubic-bezier(0.32, 0.72, 0, 1) both;
}
.amt {
  font-family: var(--display);
  font-size: 19px;
  font-weight: 600;
  color: var(--ink);
  font-feature-settings: 'tnum' 1, 'lnum' 1, 'ss01' 1;
  letter-spacing: -0.01em;
}
@keyframes g {
  from { transform: scaleX(0); transform-origin: left; }
  to   { transform: scaleX(1); }
}
@media (prefers-reduced-motion: reduce) {
  .bar span { animation: none; }
}
</style>
<div class="title">Top dukaandar</div>
<div class="list">${rows}</div>
`;
  }
}

customElements.define('hisaab-merchant-rank', HisaabMerchantRank);
